import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type GetDealPlaybookResult } from '@whinerp/client';
import { Button, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useT } from '../../app/i18n.js';

/**
 * What to ask, and what they will say back (F-056).
 *
 * Scoped to the stage the deal is in now. A rep on a call needs the four
 * questions that apply here, not the thirty the whole pipeline has.
 *
 * The stage's guidance paragraph is drawn by the checklist panel beside this
 * one and deliberately not repeated here. Two panels showing the same sentence
 * teaches people that one of them is stale.
 *
 * Drawn only when the stage has something written, following the same rule as
 * the checklist: a panel that says "nothing here" on every deal is a panel
 * people stop reading, and then miss on the stage that does have something.
 *
 * **Open by itself only while somebody is ramping.** The server decides that -
 * it knows the ramp the workspace set and when the person joined; this screen
 * knows neither. Ramp support, not permanent clutter: the rep who has been
 * here two years gets a closed panel with a word on it, and the rep in their
 * second week gets it open.
 *
 * Once somebody has opened or closed it themselves, their choice wins for as
 * long as they are looking at the deal. A panel that sprang back open on every
 * refetch would be a panel people close by closing the tab.
 */
export function PlaybookPanel({ dealId }: { dealId: string }): JSX.Element | null {
  const t = useT();
  /** null until somebody touches it; then their choice beats the default. */
  const [chosen, setChosen] = useState<boolean | null>(null);
  const playbook = useQuery<GetDealPlaybookResult, ApiError>({
    queryKey: ['deal-playbook', dealId],
    queryFn: () => api.getDealPlaybook({ path: { dealId } }),
  });

  if (playbook.isPending) return <Skeleton className="h-block-sm w-full" />;
  // Quiet on failure: this is guidance beside the work, not the work.
  if (playbook.isError) return null;

  const { questions, objections, stageLabel, expandedByDefault } = playbook.data;
  if (questions.length === 0 && objections.length === 0) return null;

  const expanded = chosen ?? expandedByDefault;

  return (
    <Panel data-region="deal-playbook" data-playbook-expanded={expanded ? 'yes' : 'no'}>
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <SectionLabel>{t('playbook.title', { values: { stage: stageLabel } })}</SectionLabel>
          <Button
            intent="ghost"
            size="small"
            className="ms-auto"
            data-playbook-toggle
            aria-expanded={expanded}
            onClick={() => setChosen(!expanded)}
          >
            {expanded ? t('playbook.hide') : t('playbook.show')}
          </Button>
        </div>

        {expanded && questions.length > 0 ? (
          <div className="flex flex-col gap-1">
            <p className="text-micro uppercase tracking-label text-ink-meta">
              {t('playbook.questions')}
            </p>
            {/* Numbered, and in the order the manager wrote them: a call has an
                order, and a set of bullets does not say which to open with. */}
            <ol className="flex list-decimal flex-col gap-1 ps-5">
              {questions.map((question, index) => (
                <li
                  key={`${index}-${question}`}
                  data-playbook-question={String(index)}
                  className="text-dense text-ink"
                >
                  {question}
                </li>
              ))}
            </ol>
          </div>
        ) : null}

        {expanded && objections.length > 0 ? (
          <div className="flex flex-col gap-2">
            <p className="text-micro uppercase tracking-label text-ink-meta">
              {t('playbook.objections')}
            </p>
            <dl className="flex flex-col gap-2">
              {objections.map((entry, index) => (
                <div
                  key={`${index}-${entry.objection}`}
                  data-playbook-objection={String(index)}
                  className="flex flex-col gap-0.5 border-s-2 border-s-rule ps-3"
                >
                  <dt className="text-dense font-medium text-ink">{entry.objection}</dt>
                  <dd className="text-dense text-ink-600">
                    <span className="text-micro uppercase tracking-label text-ink-meta">
                      {t('playbook.response')}
                    </span>{' '}
                    {entry.response}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ) : null}
      </div>
    </Panel>
  );
}
